import { BookOpen } from "lucide-react";

// Market Overview Component
const MarketOverview = () => {
  return (
    <div className="bg-white rounded-lg shadow p-6 mb-6">
      <div className="flex items-center gap-3 mb-4">
        <BookOpen className="h-6 w-6 text-green-600" />
        <h3 className="text-lg font-medium text-gray-900">Market Overview</h3>
      </div>
      <div className="prose max-w-none">
        <p className="text-gray-600">
          The U.S. food truck industry has experienced significant growth over the past decade, becoming a vital segment of the food service sector. This overview provides insights into the industry's current status, growth projections, consumer demographics, emerging trends, and challenges.
        </p>

        <strong><h4 className="text-gray-700 mt-4">Market Size & Growth</h4></strong>
        <p className="text-gray-600">
          As of 2024, the U.S. food truck industry reached an estimated <strong>$2.4 billion</strong> in revenue, reflecting a compound annual growth rate (CAGR) of <strong>10.3%</strong> over the past five years. This growth underscores the increasing consumer demand for diverse and convenient dining options. Looking ahead, the industry is projected to continue its upward trajectory, with forecasts suggesting a CAGR of <strong>6.4%</strong> from 2022 to 2030, potentially reaching <strong>$2.04 billion</strong> by 2030.
        </p>

        <strong><h4 className="text-gray-700 mt-4">Consumer Demographics</h4></strong>
        <p className="text-gray-600">
          The food truck customer base is predominantly younger, with over <strong>60%</strong> of millennials having dined at a food truck in the past year. This demographic trend highlights the importance of modern marketing strategies and trend-focused menu offerings.
        </p>

        <strong><h4 className="text-gray-700 mt-4">Industry Trends</h4></strong>
        <ul className="mt-2 ml-4 list-disc text-gray-600">
          <li>
            <strong>Customization & Specialization:</strong> Food trucks are increasingly offering unique and diverse cuisines, catering to evolving consumer preferences.
          </li>
          <li>
            <strong>Urban Integration:</strong> Cities are recognizing the economic potential of food trucks, integrating them into urban planning to boost local economies.
          </li>
          <li>
            <strong>Health-Conscious Menus:</strong> Demand for vegan, plant-based, and healthier menu options is rising among food truck patrons.
          </li>
          <li>
            <strong>Technological Integration:</strong> Geo-fencing, mobile ordering apps, and cashless payments are streamlining food truck operations and improving customer convenience.
          </li>
          <li>
            <strong>Ghost Kitchens & Delivery:</strong> Some food trucks are collaborating with ghost kitchens to expand their delivery reach beyond physical locations.
          </li>
          <li>
            <strong>Franchising Models:</strong> Larger food truck brands are entering franchising, allowing small investors to operate under established names.
          </li>
          <li>
            <strong>Eco-Friendly Operations:</strong> Food trucks are shifting toward sustainable packaging, solar-powered setups, and lower-emission vehicles.
          </li>
        </ul>

        <strong><h4 className="text-gray-700 mt-4">Challenges</h4></strong>
        <ul className="mt-2 ml-4 list-disc text-gray-600">
          <li>
            <strong>Regulatory Compliance:</strong> Licensing, health codes, and parking restrictions vary widely across cities and states, posing challenges to operators.
          </li>
          <li>
            <strong>High Competition:</strong> Dense urban markets are saturated with food trucks, increasing customer acquisition costs and reducing profit margins.
          </li>
          <li>
            <strong>Operational Costs:</strong> Rising fuel prices, food supply chain disruptions, and labor shortages are making operations more expensive.
          </li>
          <li>
            <strong>Seasonality Issues:</strong> Harsh winters and off-peak seasons impact revenue consistency, requiring food truck owners to adapt strategies.
          </li>
          <li>
            <strong>Finding Profitable Locations:</strong> As regulations tighten in major cities, food truck operators must scout for new, profitable locations with high foot traffic.
          </li>
        </ul>

        <strong><h4 className="text-gray-700 mt-4">Profitability & Future Outlook</h4></strong>
        <p className="text-gray-600">
          The average revenue per food truck is approximately <strong>$346,000</strong> per year, with profit margins around <strong>6.2%</strong>, significantly higher than traditional restaurants. The industry is expected to continue expanding due to consumer demand for convenience, unique dining experiences, and cost-effective food service models.
        </p>

        <div className="mt-6 text-sm text-gray-500">
          <strong><p className="font-semibold">Sources:</p></strong>
          <ul className="space-y-1">
            <li>
              <a href="https://www.ibisworld.com/united-states/industry/food-trucks/4322/" className="text-blue-600 hover:underline">
                IBISWorld
              </a>
            </li>
            <li>
              <a href="https://www.grandviewresearch.com/industry-analysis/us-food-truck-services-market-report" className="text-blue-600 hover:underline">
                Grand View Research
              </a>
            </li>
            <li>
              <a href="https://www.therestauranthq.com/trends/food-truck-industry-statistics/" className="text-blue-600 hover:underline">
                The Restaurant HQ
              </a>
            </li>
            <li>
              <a href="https://www.foodandwine.com/dearborn-michigan-peace-park-food-trucks-8765764" className="text-blue-600 hover:underline">
                Food & Wine
              </a>
            </li>
            <li>
              <a href="https://pos.toasttab.com/blog/on-the-line/food-truck-industry-trends-and-statistics" className="text-blue-600 hover:underline">
                Toast POS
              </a>
            </li>
            <li>
              <a href="https://www.globenewswire.com/news-release/2025/01/29/3017006/0/en/Latest-North-America-Food-Trucks-Market-is-Poised-to-Surpass-Valuation-of-US-186-22-Million-By-2033-Astute-Analytica.html" className="text-blue-600 hover:underline">
                GlobeNewswire
              </a>
            </li>
            <li>
              <a href="https://www.legionfoodtrucks.com/custom-food-trucks/food-truck-trends-and-statistics" className="text-blue-600 hover:underline">
                Legion Food Trucks
              </a>
            </li>
            <li>
              <a href="https://www.chron.com/business/technology/article/ice-cream-truck-alsies-texas-20170835.php" className="text-blue-600 hover:underline">
                Houston Chronicle
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default MarketOverview;
